import { forwardRef, useImperativeHandle, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import { Controller, SubmitHandler, UseFormReturn } from "react-hook-form"
import type { IActivityExpenseForm } from "./types"
import { useActivityExpenseForm } from "./use-form"
import { FormControl, ImageUpload, Label } from "@atoms"
import { DatePicker, Input, InputNumber, Select } from "antd"
import dayjs from "dayjs"
import {
  useExtProductionBudgetAccountCharters,
  useExtProductionBudgetActivities,
  useExtProductionBudgetVersions,
} from "pages/activity-incomes-tracking/list/queries"
import type { ISession } from "pages/assignations/preassignations/queries"
import moment from "moment"

interface Props {
  onSubmit: SubmitHandler<IActivityExpenseForm>
  defaultValues?: Partial<IActivityExpenseForm>
  children?: React.ReactNode
  session: ISession | undefined
}
export interface ActivityExpenseFormRef {
  form: UseFormReturn<IActivityExpenseForm>
}

const ActivityExpenseForm = forwardRef<ActivityExpenseFormRef, Props>(
  ({ onSubmit, defaultValues, children, session }, ref) => {
    const { t } = useTranslation()
    const [searchParams, setSearchParams] = useSearchParams()

    const [voucherImagePreview, setVoucherImagePreview] = useState<
      string | null
    >(
      typeof defaultValues?.voucher === "string" ? defaultValues?.voucher : null
    )

    const form = useActivityExpenseForm({
      ...defaultValues,
    })
    const {
      handleSubmit,
      control,
      formState: { errors },
    } = form

    useExtProductionBudgetVersions(
      {
        session_id: searchParams.get("session")!,
      },
      {
        enabled:
          !!searchParams.get("session") && !searchParams.get("charter_version"),
        onSuccess(data) {
          const defaultVersion = data.find((version) => version.is_default)
          searchParams.set("version", defaultVersion?.id!)
          searchParams.set(
            "charter_version",
            defaultVersion?.charter_version_id!
          )
          setSearchParams(searchParams)
        },
      }
    )

    const budgetActivities = useExtProductionBudgetActivities(
      {
        session_id: searchParams.get("session")!,
        version_id: searchParams.get("version")!,
      },
      {
        enabled:
          !!searchParams.get("session")! && !!searchParams.get("version")!,
      }
    )

    const posts = useExtProductionBudgetAccountCharters(
      searchParams.get("charter_version")!,
      {
        parent_type: "expenses",
      },
      {
        enabled: !!searchParams.get("charter_version"),
        select(data) {
          return data.filter((item) => !item.is_system)
        },
      }
    )

    useImperativeHandle(ref, () => ({ form }), [form])

    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormControl>
          <Label htmlFor="date">* {t("date.date")}</Label>
          <Controller
            name="date"
            control={control}
            render={({ field }) => (
              <DatePicker
                {...field}
                picker="date"
                id="date"
                status={errors.date ? "error" : ""}
                value={field.value ? dayjs(field.value) : undefined}
                onChange={(date) => {
                  field.onChange(date)
                }}
                disabled={!session}
                disabledDate={(date) =>
                  !!session &&
                  (moment(date.toDate()).valueOf() <
                    moment(`${session.start_date}T00:00:00`).valueOf() ||
                    moment(date.toDate()).valueOf() >
                      moment(`${session.end_date}T23:59:59`).valueOf())
                }
              />
            )}
          />
          {errors.date && (
            <FormControl.HelperText state="error" data-cy="date_error_message">
              {errors.date?.message}
            </FormControl.HelperText>
          )}
        </FormControl>
        <FormControl>
          <Label htmlFor="activity_id">* {t("labels.activity")}</Label>
          <Controller
            name="activity_id"
            control={control}
            render={({ field }) => {
              const options = budgetActivities.data?.map((cv) => ({
                value: cv.activity_id,
                label: cv.activity_name,
              }))
              const value =
                options?.find((cv: any) => cv.value === field.value) ?? ""
              return (
                <Select
                  {...field}
                  id="activity_id"
                  value={value}
                  status={errors.activity_id ? "error" : ""}
                  options={options}
                  loading={budgetActivities.isLoading}
                  disabled={budgetActivities.isLoading}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              )
            }}
          />
          {errors.activity_id && (
            <FormControl.HelperText
              state="error"
              data-cy="activity_id_error_message"
            >
              {errors.activity_id?.message}
            </FormControl.HelperText>
          )}
        </FormControl>
        <FormControl>
          <Label htmlFor="post_id">* {t("labels.post")}</Label>
          <Controller
            name="post_id"
            control={control}
            render={({ field }) => {
              const options = posts.data?.map((cv) => ({
                value: cv.id,
                label: cv.name,
              }))
              const value =
                options?.find((cv: any) => cv.value === field.value) ?? ""
              return (
                <Select
                  {...field}
                  id="post_id"
                  value={value}
                  status={errors.post_id ? "error" : ""}
                  options={options}
                  loading={posts.isLoading}
                  disabled={posts.isLoading}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              )
            }}
          />
          {errors.post_id && (
            <FormControl.HelperText
              state="error"
              data-cy="post_id_error_message"
            >
              {errors.post_id?.message}
            </FormControl.HelperText>
          )}
        </FormControl>
        <FormControl>
          <Label htmlFor="amount">* {t("labels.amount")}</Label>
          <Controller
            name="amount"
            control={control}
            render={({ field }) => (
              <InputNumber
                {...field}
                id="amount"
                status={errors.amount ? "error" : ""}
                style={{ width: "100%" }}
              />
            )}
          />
          {errors.amount && (
            <FormControl.HelperText
              state="error"
              data-cy="amount_error_message"
            >
              {errors.amount?.message}
            </FormControl.HelperText>
          )}
        </FormControl>
        <FormControl>
          <Label htmlFor="description">{t("labels.description")}</Label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <Input.TextArea {...field} id="description" />
            )}
          />
        </FormControl>
        <FormControl>
          <Label htmlFor="voucher">{t("labels.voucher")}</Label>
          <Controller
            name="voucher"
            control={control}
            render={({ field }) => (
              <ImageUpload
                onFileDataChange={field.onChange}
                onImageChange={setVoucherImagePreview}
                image={voucherImagePreview}
              />
            )}
          />
        </FormControl>
        <FormControl>{children}</FormControl>
      </form>
    )
  }
)

export default ActivityExpenseForm
